import React, { useState, useEffect } from 'react'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'
import AccessDenied from '../components/AccessDenied.tsx'
import MessageBanner from '../components/MessageBanner.tsx'

interface LinkedCompany {
  id: string
  companies: {
    id: string
    name: string
  }
  can_read: boolean
  can_write: boolean
}

interface Agency {
  id: string
  name: string
  company_agency_access: LinkedCompany[]
}

interface Industry {
  id: string
  name: string
}

interface Company {
  id: string
  name: string
  google_place_id?: string
  type?: string
  industries: {
    id: string
    name: string
  } | null
  address_line?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
}

const AdminAgencyCompanyAccess: React.FC = () => {
  const { user } = useAuth()
  const [agencies, setAgencies] = useState<Agency[]>([])
  const [allCompanies, setAllCompanies] = useState<Company[]>([])
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([])
  const [selectedAgency, setSelectedAgency] = useState<string>('')
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set())
  const [canRead, setCanRead] = useState(true)
  const [canWrite, setCanWrite] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Remove state
  const [selectedRemoveIds, setSelectedRemoveIds] = useState<Set<string>>(new Set())
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [removeLoading, setRemoveLoading] = useState(false)
  const [removeSearchTerm, setRemoveSearchTerm] = useState('')

  // Edit company state
  const [industries, setIndustries] = useState<Industry[]>([])
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [editForm, setEditForm] = useState({
    name: '',
    type: 'CLIENT',
    industry_id: '',
    address_line: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  })
  const [editLoading, setEditLoading] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [agenciesResponse, companiesResponse, industriesResponse] = await Promise.all([
        api.get('/api/admin/agencies'),
        api.get('/api/admin/companies'),
        api.get('/api/agency/industries'),
      ])

      setAgencies(agenciesResponse.data.agencies)
      setAllCompanies(companiesResponse.data.companies)
      setAvailableCompanies(companiesResponse.data.companies)
      setIndustries(industriesResponse.data.industries || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('Failed to load data')
    }
  }

  const fetchCompaniesWithoutAgency = async (agencyId: string) => {
    try {
      const response = await api.get(`/api/admin/agencies/${agencyId}/available-companies`)
      setAvailableCompanies(response.data.companies)
    } catch (error: any) {
      console.error('Error fetching available companies:', error)
      setError(
        `Failed to load available companies: ${error.response?.data?.message || error.message}`,
      )
    }
  }

  const handleAgencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setSelectedAgency(value)
    setSelectedCompanyIds(new Set())
    setSelectedRemoveIds(new Set())
    setSearchTerm('')
    setRemoveSearchTerm('')
    if (value) {
      fetchCompaniesWithoutAgency(value)
    } else {
      setAvailableCompanies(allCompanies)
    }
  }

  // Get linked companies for the selected agency
  const currentAgency = agencies.find(a => a.id === selectedAgency)
  const linkedCompanies = currentAgency?.company_agency_access || []

  const filteredLinkedCompanies = linkedCompanies.filter(lc =>
    lc.companies.name.toLowerCase().includes(removeSearchTerm.toLowerCase()),
  )

  // --- Grant logic ---
  const toggleCompany = (companyId: string) => {
    setSelectedCompanyIds(prev => {
      const next = new Set(prev)
      if (next.has(companyId)) {
        next.delete(companyId)
      } else {
        next.add(companyId)
      }
      return next
    })
  }

  const filteredCompanies = availableCompanies.filter(
    c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.industries?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const toggleAll = () => {
    if (selectedCompanyIds.size === filteredCompanies.length && filteredCompanies.length > 0) {
      setSelectedCompanyIds(new Set())
    } else {
      setSelectedCompanyIds(new Set(filteredCompanies.map(c => c.id)))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowConfirm(true)
  }

  const handleConfirm = async () => {
    setShowConfirm(false)
    setLoading(true)
    setSuccess('')
    setError('')

    try {
      await api.post('/api/admin/agency-company-access', {
        agencyId: selectedAgency,
        companyIds: Array.from(selectedCompanyIds),
        canRead,
        canWrite,
      })

      const count = selectedCompanyIds.size
      setSuccess(
        `Access granted for ${count} ${count === 1 ? 'company' : 'companies'} successfully!`,
      )
      setSelectedCompanyIds(new Set())
      setSearchTerm('')
      // Refresh both agency data (for linked list) and available companies
      await fetchData()
      if (selectedAgency) {
        fetchCompaniesWithoutAgency(selectedAgency)
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to grant access')
    } finally {
      setLoading(false)
    }
  }

  // --- Remove logic ---
  const toggleRemoveCompany = (companyId: string) => {
    setSelectedRemoveIds(prev => {
      const next = new Set(prev)
      if (next.has(companyId)) {
        next.delete(companyId)
      } else {
        next.add(companyId)
      }
      return next
    })
  }

  const toggleAllRemove = () => {
    if (
      selectedRemoveIds.size === filteredLinkedCompanies.length &&
      filteredLinkedCompanies.length > 0
    ) {
      setSelectedRemoveIds(new Set())
    } else {
      setSelectedRemoveIds(new Set(filteredLinkedCompanies.map(lc => lc.companies.id)))
    }
  }

  const handleRemoveConfirm = async () => {
    setShowRemoveConfirm(false)
    setRemoveLoading(true)
    setSuccess('')
    setError('')

    try {
      await api.delete('/api/admin/agency-company-access', {
        data: {
          agencyId: selectedAgency,
          companyIds: Array.from(selectedRemoveIds),
        },
      })

      const count = selectedRemoveIds.size
      setSuccess(
        `Access revoked for ${count} ${count === 1 ? 'company' : 'companies'} successfully!`,
      )
      setSelectedRemoveIds(new Set())
      setRemoveSearchTerm('')
      // Refresh both agency data (for linked list) and available companies
      await fetchData()
      if (selectedAgency) {
        fetchCompaniesWithoutAgency(selectedAgency)
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to revoke access')
    } finally {
      setRemoveLoading(false)
    }
  }

  const selectedAgencyName = agencies.find(a => a.id === selectedAgency)?.name || ''

  const selectedCompanyNames = availableCompanies
    .filter(c => selectedCompanyIds.has(c.id))
    .map(c => c.name)

  const selectedRemoveNames = linkedCompanies
    .filter(lc => selectedRemoveIds.has(lc.companies.id))
    .map(lc => lc.companies.name)

  const handleEditCompany = (company: Company) => {
    setEditForm({
      name: company.name,
      type: company.type || 'CLIENT',
      industry_id: company.industries?.id || '',
      address_line: company.address_line || '',
      city: company.city || '',
      state: company.state || '',
      postal_code: company.postal_code || '',
      country: company.country || '',
    })
    setEditingCompany(company)
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCompany) return
    setEditLoading(true)
    setSuccess('')
    setError('')

    try {
      await api.put(`/api/admin/companies/${editingCompany.id}`, editForm)
      setSuccess('Company updated successfully!')
      setEditingCompany(null)
      await fetchData()
      if (selectedAgency) {
        fetchCompaniesWithoutAgency(selectedAgency)
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update company')
    } finally {
      setEditLoading(false)
    }
  }

  if (!user?.is_system_admin) {
    return <AccessDenied />
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Agency-Company Connection</h1>

          <MessageBanner message={success} error={error} />

          {/* Agency selector */}
          <div className="mb-6">
            <label htmlFor="agency" className="block text-sm font-medium text-gray-700 mb-2">
              Agency *
            </label>
            <select
              id="agency"
              value={selectedAgency}
              onChange={handleAgencyChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an agency</option>
              {agencies.map(agency => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          </div>

          {/* Current Access section — shown when agency is selected and has linked companies */}
          {selectedAgency && linkedCompanies.length > 0 && (
            <div className="mb-8 border border-gray-200 rounded-lg p-5 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Current Access</h2>
              <p className="text-sm text-gray-500 mb-3">
                {linkedCompanies.length} {linkedCompanies.length === 1 ? 'company' : 'companies'}{' '}
                linked to {selectedAgencyName}
              </p>

              {/* Search + select all */}
              <div className="flex items-center gap-3 mb-2">
                <input
                  type="text"
                  placeholder="Search linked companies..."
                  value={removeSearchTerm}
                  onChange={e => setRemoveSearchTerm(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={toggleAllRemove}
                  className="text-sm text-red-600 hover:text-red-800 whitespace-nowrap"
                >
                  {selectedRemoveIds.size === filteredLinkedCompanies.length &&
                  filteredLinkedCompanies.length > 0
                    ? 'Deselect all'
                    : 'Select all'}
                </button>
              </div>

              {/* Scrollable checkbox list */}
              <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto bg-white">
                {filteredLinkedCompanies.map(lc => (
                  <label
                    key={lc.id}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-red-50 border-b border-gray-100 last:border-b-0 ${
                      selectedRemoveIds.has(lc.companies.id) ? 'bg-red-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRemoveIds.has(lc.companies.id)}
                      onChange={() => toggleRemoveCompany(lc.companies.id)}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">{lc.companies.name}</span>
                    <button
                      type="button"
                      onClick={e => {
                        e.preventDefault()
                        const company = allCompanies.find(c => c.id === lc.companies.id)
                        if (company) handleEditCompany(company)
                      }}
                      className="ml-2 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit company"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </button>
                    <span className="ml-auto text-xs text-gray-500">
                      {[lc.can_read && 'Read', lc.can_write && 'Write'].filter(Boolean).join(', ')}
                    </span>
                  </label>
                ))}
                {filteredLinkedCompanies.length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-500 text-center">
                    No companies match your search
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setShowRemoveConfirm(true)}
                disabled={removeLoading || selectedRemoveIds.size === 0}
                className="mt-3 w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {removeLoading
                  ? 'Removing...'
                  : `Remove Access${selectedRemoveIds.size > 0 ? ` (${selectedRemoveIds.size} compan${selectedRemoveIds.size === 1 ? 'y' : 'ies'})` : ''}`}
              </button>
            </div>
          )}

          {/* No linked companies message */}
          {selectedAgency && linkedCompanies.length === 0 && (
            <div className="mb-8 border border-gray-200 rounded-lg p-5 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Current Access</h2>
              <p className="text-sm text-gray-500 italic">
                No companies linked to {selectedAgencyName}
              </p>
            </div>
          )}

          {/* Grant Access section */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Grant Access</h2>

            {/* Company multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Companies
                {selectedCompanyIds.size > 0 && (
                  <span className="ml-2 text-blue-600 font-normal">
                    ({selectedCompanyIds.size} selected)
                  </span>
                )}
              </label>

              {!selectedAgency ? (
                <p className="text-sm text-gray-500 italic py-4">Select an agency first</p>
              ) : availableCompanies.length === 0 ? (
                <p className="text-sm text-gray-500 italic py-4">
                  No available companies for this agency
                </p>
              ) : (
                <>
                  {/* Search + select all */}
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="text"
                      placeholder="Search companies..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      {selectedCompanyIds.size === filteredCompanies.length &&
                      filteredCompanies.length > 0
                        ? 'Deselect all'
                        : 'Select all'}
                    </button>
                  </div>

                  {/* Scrollable checkbox list */}
                  <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                    {filteredCompanies.map(company => (
                      <label
                        key={company.id}
                        className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                          selectedCompanyIds.has(company.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCompanyIds.has(company.id)}
                          onChange={() => toggleCompany(company.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm text-gray-900">{company.name}</span>
                        <button
                          type="button"
                          onClick={e => {
                            e.preventDefault()
                            handleEditCompany(company)
                          }}
                          className="ml-2 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit company"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </button>
                        <span className="ml-auto text-xs text-gray-500">
                          {company.industries?.name}
                        </span>
                      </label>
                    ))}
                    {filteredCompanies.length === 0 && (
                      <p className="px-3 py-4 text-sm text-gray-500 text-center">
                        No companies match your search
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3 text-xs text-blue-800">
                <p className="font-medium mb-1">Permission levels:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>
                    <strong>Read</strong> — View company metrics, reports, and analytics. Required
                    to see the company in dashboards.
                  </li>
                  <li>
                    <strong>Write</strong> — Edit company details, manage keywords, and modify
                    settings. Automatically includes read access.
                  </li>
                  <li>
                    <strong>Neither</strong> — Leave both Read and Write unchecked. The company link
                    is preserved but the agency cannot see or manage it (no access).
                  </li>
                </ul>
              </div>
              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={canRead}
                    disabled={canWrite}
                    onChange={e => setCanRead(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5 disabled:opacity-50"
                  />
                  <div className="ml-2">
                    <span className="text-sm text-gray-900">Read Access</span>
                    <p className="text-xs text-gray-500">
                      View metrics, reports, and analytics for this company
                      {canWrite && (
                        <span className="text-blue-600 ml-1">(required by write access)</span>
                      )}
                    </p>
                  </div>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={canWrite}
                    onChange={e => {
                      setCanWrite(e.target.checked)
                      if (e.target.checked) setCanRead(true)
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5"
                  />
                  <div className="ml-2">
                    <span className="text-sm text-gray-900">Write Access</span>
                    <p className="text-xs text-gray-500">
                      Edit company details, manage keywords, and modify settings
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !selectedAgency || selectedCompanyIds.size === 0}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? 'Granting Access...'
                : `Grant Access${selectedCompanyIds.size > 0 ? ` (${selectedCompanyIds.size} compan${selectedCompanyIds.size === 1 ? 'y' : 'ies'})` : ''}`}
            </button>
          </form>
        </div>
      </div>

      {/* Grant confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Confirm Access Grant</h2>

            <p className="text-sm text-gray-700 mb-3">
              You are about to grant the following access:
            </p>

            <div className="bg-gray-50 rounded-md p-4 mb-4 space-y-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">Agency:</span>{' '}
                <span className="text-gray-900">{selectedAgencyName}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  Companies ({selectedCompanyNames.length}):
                </span>
                <ul className="mt-1 ml-4 list-disc text-gray-900 max-h-40 overflow-y-auto">
                  {selectedCompanyNames.map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="font-medium text-gray-700">Permissions:</span>{' '}
                <span className="text-gray-900">
                  {[canRead && 'Read', canWrite && 'Write'].filter(Boolean).join(', ') || 'None'}
                </span>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove confirmation modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-lg font-bold text-red-700 mb-4">Confirm Access Removal</h2>

            <p className="text-sm text-gray-700 mb-3">
              You are about to revoke access for the following companies from{' '}
              <strong>{selectedAgencyName}</strong>:
            </p>

            <div className="bg-red-50 rounded-md p-4 mb-4 text-sm">
              <ul className="ml-4 list-disc text-gray-900 max-h-40 overflow-y-auto">
                {selectedRemoveNames.map((name, i) => (
                  <li key={i}>{name}</li>
                ))}
              </ul>
            </div>

            <p className="text-sm text-red-600 mb-4">
              Agency users will immediately lose access to these companies.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Remove Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit company modal */}
      {editingCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Edit Company</h2>
            <p className="text-sm text-gray-500 mb-4">
              {editingCompany.google_place_id && (
                <span>
                  Google Place ID:{' '}
                  <code className="bg-gray-100 px-1 rounded text-xs">
                    {editingCompany.google_place_id}
                  </code>
                </span>
              )}
            </p>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={editForm.type}
                    onChange={e => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CLIENT">Client</option>
                    <option value="COMPETITOR">Competitor</option>
                    <option value="PARTNER">Partner</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <select
                    value={editForm.industry_id}
                    onChange={e => setEditForm(prev => ({ ...prev, industry_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No industry</option>
                    {industries.map(ind => (
                      <option key={ind.id} value={ind.id}>
                        {ind.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={editForm.address_line}
                    onChange={e => setEditForm(prev => ({ ...prev, address_line: e.target.value }))}
                    placeholder="Street address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={e => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={editForm.state}
                    onChange={e => setEditForm(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={editForm.postal_code}
                    onChange={e => setEditForm(prev => ({ ...prev, postal_code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={editForm.country}
                    onChange={e => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="e.g. US"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setEditingCompany(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editLoading ? 'Updating...' : 'Update Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminAgencyCompanyAccess
